const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Types
export interface LedgerType {
    id: number;
    slug: string;
    name: string;
}

export interface LedgerEntry {
    id: number;
    date: string;
    formatted_date: string;
    description: string;
    head_of_account: string;
    debit: number;
    credit: number;
    balance: number;
    user: {
        id: number;
        name: string;
    } | null;
    branch: {
        id: number;
        name: string;
        code: string;
    } | null;
    created_at: string;
}

export interface LedgerTotals {
    debit: number;
    credit: number;
}

export interface LedgerPagination {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
}

export interface LedgerResponse {
    ledger_type: {
        id: number;
        slug: string;
        name: string;
    };
    entries: LedgerEntry[];
    totals: LedgerTotals;
    date_range: {
        from: string | null;
        to: string | null;
    };
    pagination: LedgerPagination;
}

export interface LedgerSummaryItem {
    type: string;
    name: string;
    balance: number;
    total_debit: number;
    total_credit: number;
}

export interface LedgerFilters {
    type: string;
    from_date?: string;
    to_date?: string;
    per_page?: number;
    page?: number;
}

interface BackendResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

// Helper function to handle API errors
const handleApiError = async (response: Response): Promise<never> => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
};

export const headOfficeFinanceApi = {
    // Get all ledger types
    getLedgerTypes: async (): Promise<LedgerType[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/head-office/ledger-types`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ ledger_types: LedgerType[] }> = await response.json();
        return data.data.ledger_types;
    },

    // Get ledger entries with filters
    getLedger: async (filters: LedgerFilters): Promise<LedgerResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const params = new URLSearchParams();
        params.append('type', filters.type);
        if (filters.from_date) params.append('from_date', filters.from_date);
        if (filters.to_date) params.append('to_date', filters.to_date);
        if (filters.per_page) params.append('per_page', filters.per_page.toString());
        if (filters.page) params.append('page', filters.page.toString());

        const response = await fetch(`${API_BASE_URL}/head-office/ledger?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<LedgerResponse> = await response.json();
        return data.data;
    },

    // Get summary of all ledger types
    getSummary: async (): Promise<LedgerSummaryItem[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/head-office/summary`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ summary: LedgerSummaryItem[] }> = await response.json();
        return data.data.summary;
    },
};
